import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp } from 'lucide-react';

const ScrollButton = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [sections, setSections] = useState([]);
  const [scrollDirection, setScrollDirection] = useState('down');
  const [isScrolling, setIsScrolling] = useState(false);

  // Find all section elements on component mount
  useEffect(() => {
    const sectionElements = Array.from(document.querySelectorAll('div[id]')).filter(el => {
      // Filter only the main sections
      const validIds = ['main', 'sobre', 'caracteristicas', 'ubicacion', 'contacto'];
      return validIds.includes(el.id);
    });
    
    // Sort sections by their vertical position
    const sortedSections = sectionElements.sort((a, b) => {
      return a.getBoundingClientRect().top - b.getBoundingClientRect().top;
    });
    
    setSections(sortedSections);
    
    let lastScrollY = window.scrollY;
    
    // Handle scroll event to determine current section and scroll direction
    const handleScroll = () => {
      const scrollY = window.scrollY;
      
      // Determine scroll direction
      if (scrollY > lastScrollY) {
        setScrollDirection('down');
      } else {
        setScrollDirection('up');
      }
      lastScrollY = scrollY;
      
      // Only show button if we've scrolled down a bit and not currently executing a scroll
      setIsVisible(scrollY > 300 && !isScrolling);
      
      // Find current section
      const scrollPosition = scrollY + window.innerHeight / 3;
      for (let i = sortedSections.length - 1; i >= 0; i--) {
        const section = sortedSections[i];
        const sectionTop = section.getBoundingClientRect().top + scrollY;
        
        if (scrollPosition >= sectionTop) {
          setCurrentSectionIndex(i);
          break;
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial call
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isScrolling]);

  // Scroll to next or previous section
  const scrollToNextSection = () => {
    if (isScrolling) return;
    
    const nextIndex = scrollDirection === 'down' 
      ? Math.min(currentSectionIndex + 1, sections.length - 1)
      : Math.max(currentSectionIndex - 1, 0);
    
    // Don't do anything if we're at the boundaries
    if (nextIndex === currentSectionIndex) return;
    
    const nextSection = sections[nextIndex];
    
    if (nextSection) {
      setIsScrolling(true);
      
      // Calculate position to center the section
      const viewportHeight = window.innerHeight;
      const elementHeight = nextSection.getBoundingClientRect().height;
      const offset = Math.max(0, (viewportHeight - elementHeight) / 2);
      
      // Smooth scroll to the element
      window.scrollTo({
        top: nextSection.offsetTop - offset,
        behavior: 'smooth'
      });
      
      // Reset scrolling state after animation completes
      setTimeout(() => setIsScrolling(false), 1000);
    }
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.button
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={scrollToNextSection}
          className="fixed bottom-8 right-8 z-40 p-3 rounded-full
                    bg-blue-500 text-white shadow-lg
                    transition-all duration-300 hover:bg-blue-600"
        >
          {scrollDirection === 'down' ? (
            <ChevronDown className="w-6 h-6" />
          ) : (
            <ChevronUp className="w-6 h-6" />
          )}
        </motion.button>
      )}
    </AnimatePresence>
  );
};

export default ScrollButton;